import type {ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {Grid3x3, Mic, MicOff, Pause, Play, SlidersHorizontal, Volume2} from 'lucide-react';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import type {ControlSpec} from '../group/useGroupControls';
import {matrixSurfaceMessages as dm} from '../matrix/matrixMessages';
import type {PhoneCallLeg} from '../model/types';
import type {PhoneActions} from './usePhoneActions';

interface PhoneControlOptions {
    readonly iconSize: number;
    readonly speakerOpen: boolean;
    readonly onKeypad: () => void;
    readonly onSpeaker: () => void;
    readonly onDevices: () => void;
}

export interface PhoneControls {
    readonly microphone: ControlSpec;
    readonly keypad: ControlSpec;
    readonly speaker: ControlSpec;
    readonly hold: ControlSpec;
    readonly devices: ControlSpec;
}

export const usePhoneControls = (leg: PhoneCallLeg, actions: PhoneActions, options: PhoneControlOptions): PhoneControls => {
    const intl = useIntl();
    const icon = (node: (size: number) => ReactNode) => node(options.iconSize);

    const microphone: ControlSpec = {
        id: 'microphone',
        testId: 'phone-call-mic',
        icon: leg.muted ? icon(size => <MicOff size={size} aria-hidden="true"/>) : icon(size => <Mic size={size} aria-hidden="true"/>),
        label: intl.formatMessage(leg.muted ? gm.controlUnmute : gm.controlMute),
        caption: intl.formatMessage(leg.muted ? gs.captionUnmute : gs.captionMute),
        tone: leg.muted ? 'pressed' : 'default',
        disabled: leg.held,
        onClick: actions.toggleMute,
    };

    const keypad: ControlSpec = {
        id: 'keypad',
        testId: 'phone-call-keypad',
        icon: icon(size => <Grid3x3 size={size} aria-hidden="true"/>),
        label: intl.formatMessage(dm.keypadAria),
        caption: intl.formatMessage(dm.captionKeypad),
        tone: 'default',
        hasPopup: 'dialog',
        onClick: options.onKeypad,
    };

    const speaker: ControlSpec = {
        id: 'speaker',
        testId: 'phone-call-speaker',
        icon: icon(size => <Volume2 size={size} aria-hidden="true"/>),
        label: intl.formatMessage(dm.speakerAria),
        caption: intl.formatMessage(dm.captionSpeaker),
        tone: options.speakerOpen ? 'pressed' : 'default',
        expanded: options.speakerOpen,
        hasPopup: 'menu',
        onClick: options.onSpeaker,
    };

    const hold: ControlSpec = {
        id: 'hold',
        testId: 'phone-call-hold',
        icon: leg.held ? icon(size => <Play size={size} aria-hidden="true"/>) : icon(size => <Pause size={size} aria-hidden="true"/>),
        label: intl.formatMessage(leg.held ? dm.resumeAria : dm.holdAria),
        caption: intl.formatMessage(leg.held ? dm.captionResume : dm.captionHold),
        tone: leg.held ? 'pressed' : 'default',
        onClick: actions.toggleHold,
    };

    const devices: ControlSpec = {
        id: 'devices',
        testId: 'phone-call-settings',
        icon: icon(size => <SlidersHorizontal size={size} aria-hidden="true"/>),
        label: intl.formatMessage(gs.deviceSettings),
        caption: intl.formatMessage(gs.captionDevices),
        tone: 'default',
        hasPopup: 'dialog',
        onClick: options.onDevices,
    };

    return {microphone, keypad, speaker, hold, devices};
};
